import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import {
  getTodayVsYesterday,
  getYesterdayVsPriorDay,
  isRangeValid,
  matchesWorkspaceDatePreset,
  resolveWorkspaceDatePreset,
  type DateRange,
} from '@/api/reportDateRanges';
import {
  WORKSPACE_DATE_PRESETS,
  type WorkspaceDatePresetId,
} from '@/constants/Constant_Preset_Variables';

export type SnapshotRangeState = {
  current: DateRange;
  previous: DateRange;
  compareEnabled: boolean;
};

type PresetId = WorkspaceDatePresetId;

const PRESETS = WORKSPACE_DATE_PRESETS;

function resolvePreset(id: PresetId): { current: DateRange; previous: DateRange } {
  return resolveWorkspaceDatePreset(id);
}

function formatRangeLabel(range: DateRange): string {
  if (range.startDate === range.endDate) {
    return new Date(range.startDate + 'T12:00:00').toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  const fmt = (iso: string) =>
    new Date(iso + 'T12:00:00').toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
    });
  return `${fmt(range.startDate)} – ${fmt(range.endDate)}`;
}

export function getDefaultSnapshotRange(): SnapshotRangeState {
  const { current, previous } = getYesterdayVsPriorDay();
  return { current, previous, compareEnabled: true };
}

/** Workspace always compares the current range to a previous period. */
export function getDefaultWorkspaceRange(): SnapshotRangeState {
  const { current, previous } = getYesterdayVsPriorDay();
  return { current, previous, compareEnabled: true };
}

/** Workspace and Dashboard default: today vs yesterday (local calendar). */
export function getDefaultWorkspaceDashboardRange(): SnapshotRangeState {
  const { current, previous } = getTodayVsYesterday();
  return { current, previous, compareEnabled: true };
}

export function SnapshotDateRangePicker({
  value,
  onChange,
}: {
  value: SnapshotRangeState;
  onChange: (next: SnapshotRangeState) => void;
}) {
  const [open, setOpen] = useState(false);
  const activePreset =
    WORKSPACE_DATE_PRESETS.find((p) =>
      matchesWorkspaceDatePreset(p.id, value.current, value.previous)
    )?.id ?? 'yesterday';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const applyPreset = (id: PresetId) => {
    const { current, previous } = resolvePreset(id);
    onChange({ ...value, current, previous });
    setOpen(false);
  };

  const triggerLabel = value.compareEnabled
    ? `${formatRangeLabel(value.current)} vs ${formatRangeLabel(value.previous)}`
    : formatRangeLabel(value.current);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="secondary" size="sm" className="gap-2 font-normal">
            <CalendarIcon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="max-w-[min(320px,50vw)] truncate text-left">{triggerLabel}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(360px,calc(100vw-2rem))] p-0" align="end">
          <div className="flex">
            <div className="w-36 shrink-0 border-r border-border bg-surface-muted/50 p-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  className={cn(
                    'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                    activePreset === p.id
                      ? 'bg-brand/10 font-semibold text-brand'
                      : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex-1 p-4 text-sm">
              <p className="font-semibold text-foreground">Current period</p>
              <p className="mt-1 text-foreground-muted">
                {value.current.startDate} → {value.current.endDate}
              </p>
              {value.compareEnabled ? (
                <>
                  <p className="mt-4 font-semibold text-foreground">Compared to</p>
                  <p className="mt-1 text-foreground-muted">
                    {value.previous.startDate} → {value.previous.endDate}
                  </p>
                </>
              ) : null}
              <p className="mt-4 text-xs text-foreground-muted">{timezone}</p>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground-muted">
        <input
          type="checkbox"
          checked={value.compareEnabled}
          onChange={(e) => onChange({ ...value, compareEnabled: e.target.checked })}
          className="rounded border-border"
        />
        Compare
      </label>
    </div>
  );
}

export function snapshotRangesValid(state: SnapshotRangeState): boolean {
  if (!isRangeValid(state.current)) return false;
  if (!state.compareEnabled) return true;
  return isRangeValid(state.previous);
}
